import DOMPurify from "dompurify";

// Whitelist tags and attributes we ever render from markdown/chat/AI content.
const SAFE_CONFIG = {
  ALLOWED_TAGS: [
    "a", "b", "strong", "i", "em", "u", "br", "p", "div", "span",
    "ul", "ol", "li", "code", "pre", "h1", "h2", "h3", "h4", "h5", "h6",
    "blockquote", "hr", "img", "table", "thead", "tbody", "tr", "th", "td",
  ],
  ALLOWED_ATTR: ["href", "title", "target", "rel", "class", "src", "alt"],
  ALLOWED_URI_REGEXP: /^(?:https?|mailto):/i,
};

// Force safe link attributes on any anchor we render.
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A") {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
});

/**
 * Sanitize untrusted HTML before feeding it to dangerouslySetInnerHTML.
 * Blocks scripts, event handlers, javascript: urls, iframe, object etc.
 */
export function sanitizeHtml(html) {
  if (!html) return "";
  return DOMPurify.sanitize(String(html), SAFE_CONFIG);
}

// A generated status report is a whole standalone document: it carries its own
// <style> block, a Google Fonts <link>, and inline style="flex:N" on the stage
// rails. SAFE_CONFIG above strips all three (no style tag, no style attr), so
// reports need their own config rather than reusing it.
//
// This output is only ever written to a sandboxed iframe's srcDoc (see
// ReportsPage) — the sandbox, not this allowlist, is the real boundary against
// script execution. Stripping script/iframe/object/form here is defence in
// depth so a downloaded copy opened directly in a browser is also inert.
const REPORT_DOC_CONFIG = {
  WHOLE_DOCUMENT: true,
  ALLOWED_TAGS: [
    "html", "head", "body", "meta", "title", "link", "style",
    "header", "footer", "section", "article", "nav", "aside", "main",
    "div", "span", "p", "br", "hr", "a", "b", "strong", "i", "em", "u", "small",
    "ul", "ol", "li", "dl", "dt", "dd", "code", "pre", "blockquote", "figure", "figcaption",
    "h1", "h2", "h3", "h4", "h5", "h6",
    "table", "thead", "tbody", "tfoot", "tr", "th", "td", "caption", "colgroup", "col",
    "img", "svg", "path", "g", "circle", "rect", "line", "polyline", "polygon", "text",
  ],
  ALLOWED_ATTR: [
    "class", "style", "id", "title", "lang", "dir",
    "href", "target", "rel", "src", "alt", "width", "height",
    "colspan", "rowspan", "scope", "span",
    "charset", "name", "content", "crossorigin",
    "viewBox", "d", "fill", "stroke", "stroke-width", "cx", "cy", "r", "x", "y", "x1", "y1", "x2", "y2", "points", "transform", "opacity",
  ],
  // Deliberately NOT overriding ALLOWED_URI_REGEXP. DOMPurify tests every
  // attribute outside its URI_SAFE_ATTRIBUTES list against that regex — and
  // colspan/rowspan/width are not on it. The built-in default tolerates them
  // via an `[^a-z]` alternative that matches plain numbers; a hand-written
  // scheme-only regex silently strips `colspan="10"` and collapses the sample's
  // banded tables. Instead: keep the default (which already blocks javascript:
  // and bare data:) and widen it only for images, the idiomatic way.
  ADD_DATA_URI_TAGS: ["img"],
  FORBID_TAGS: ["script", "iframe", "object", "embed", "form", "input", "button", "select", "textarea", "base", "noscript"],
  ALLOW_DATA_ATTR: false,
};

/**
 * Sanitize a full generated report document for iframe srcDoc / download.
 * Keeps the report's own <style> and inline styles; drops anything executable.
 */
export function sanitizeReportDocument(html) {
  if (!html) return "";
  return DOMPurify.sanitize(String(html), REPORT_DOC_CONFIG);
}
